import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Switch from '../../../components/Switch';
import TextArea from '../../../components/TextArea';
import { ROLE_DEFAULT } from '../../../constants';
import { getRoleNameError, getRoleDescriptionError } from '../../../utils/validation';

/**
 * Transform permissions from API format to flat object format
 * Input: [{ role: [{ key: "addRole", value: true }, ...] }, { user: [...] }]
 * Output: { role: { addRole: true, deleteRole: false }, user: { addUser: true } }
 */
function transformPermissionsFromAPI(apiPermissions) {
  if (!apiPermissions || !Array.isArray(apiPermissions)) return {};

  const result = {};
  apiPermissions.forEach((categoryObj) => {
    const categoryName = Object.keys(categoryObj)[0];
    const permissions = categoryObj[categoryName];

    if (Array.isArray(permissions)) {
      result[categoryName] = {};
      permissions.forEach((perm) => {
        result[categoryName][perm.key] = perm.value || false;
      });
    }
  });

  return result;
}

/**
 * Add/Edit Role Modal Component with Permission Checkboxes
 *
 * @param {Boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onConfirm - Callback when role is confirmed with role data
 * @param {Object} roleData - Role data for editing (null for adding)
 * @param {Array} defaultPermissions - Default permissions from /roles/init
 * @param {Array} modules - Module metadata (title, description) from /roles/init
 * @param {Boolean} editMode - Whether in edit mode
 */
function AddRoleModal({ open, onClose, onConfirm, roleData = null, defaultPermissions = null, modules = null, editMode = false }) {
  // Check if role is a default role (type === ROLE_DEFAULT)
  const isDefaultRole = editMode && roleData && roleData.type === ROLE_DEFAULT;
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });
  const [permissions, setPermissions] = useState({});
  const [errors, setErrors] = useState({});

  // Initialize permissions and form data
  useEffect(() => {
    if (editMode && roleData) {
      // Edit mode: use role's permissions
      setFormData({
        name: roleData.name || '',
        description: roleData.description || '',
        isActive: roleData.isActive !== undefined ? roleData.isActive : true,
      });

      // Transform permissions from role data
      const transformedPermissions = transformPermissionsFromAPI(roleData.permissions);
      setPermissions(transformedPermissions);
    } else if (defaultPermissions) {
      // Add mode: use default permissions (all false)
      setFormData({
        name: '',
        description: '',
        isActive: true,
      });

      const transformedPermissions = transformPermissionsFromAPI(defaultPermissions);
      setPermissions(transformedPermissions);
    } else {
      // No permissions available
      setFormData({
        name: '',
        description: '',
        isActive: true,
      });
      setPermissions({});
    }
    setErrors({});
  }, [editMode, roleData, defaultPermissions, open]);

  // Handle input change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Validate field in real-time
    const newErrors = { ...errors };

    if (field === 'name') {
      const nameError = getRoleNameError(value, t);
      if (nameError) {
        newErrors.name = nameError;
      } else {
        delete newErrors.name;
      }
    } else if (field === 'description') {
      const descriptionError = getRoleDescriptionError(value, t);
      if (descriptionError) {
        newErrors.description = descriptionError;
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  // Handle switch change
  const handleSwitchChange = (event) => {
    setFormData({
      ...formData,
      isActive: event.target.checked,
    });
  };

  // Handle permission checkbox change
  const handlePermissionChange = (category, permissionKey) => (event) => {
    setPermissions((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permissionKey]: event.target.checked,
      },
    }));
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.name.trim() !== '';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    const nameError = getRoleNameError(formData.name, t);
    if (nameError) newErrors.name = nameError;

    const descriptionError = getRoleDescriptionError(formData.description, t);
    if (descriptionError) newErrors.description = descriptionError;

    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    const dataToSend = {
      ...formData,
      permissions,
    };

    if (editMode && roleData) {
      dataToSend.id = roleData.id;
    }

    onConfirm(dataToSend);
    handleClose();
  };

  // Handle close and reset form
  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      isActive: true,
    });
    setPermissions({});
    onClose();
  };

  // Get permission details from default permissions
  const getPermissionDetails = (category, permissionKey) => {
    if (!defaultPermissions) return null;

    const categoryData = defaultPermissions.find((cat) => Object.keys(cat)[0] === category);
    if (!categoryData) return null;

    const perms = categoryData[category];
    const perm = perms.find((p) => p.key === permissionKey);
    return perm || null;
  };

  // Get module metadata (title, description) for a category
  const getModuleMetadata = (category) => {
    if (!modules) return null;

    const moduleData = modules.find((mod) => mod.module === category);
    return moduleData || null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('roles.editDialog.title') : t('roles.addDialog.title')}
      </DialogTitle>
      <DialogContent
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Basic Information - Fixed at top */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, flexShrink: 0 }}>
          <Input
            label={t('roles.addDialog.name')}
            value={formData.name}
            onChange={handleChange('name')}
            required
            autoFocus
            error={!!errors.name}
            helperText={errors.name}
            id="name"
            name="name"
            data-testid="role-modal-input-name"
          />
          <TextArea
            label={t('roles.addDialog.description')}
            value={formData.description}
            onChange={handleChange('description')}
            rows={2}
            error={!!errors.description}
            helperText={errors.description}
            id="description"
            name="description"
            data-testid="role-modal-input-description"
          />
          <Switch
            label={formData.isActive ? t('roles.addDialog.active') : t('roles.addDialog.inactive')}
            checked={formData.isActive}
            onChange={handleSwitchChange}
            disabled={isDefaultRole}
            id="isActive"
            name="isActive"
            data-testid="role-modal-switch-isActive"
          />

          <Divider sx={{ my: 1 }} />

          {/* Permissions Section Title */}
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0 }}>
            {t('roles.addDialog.permissions')}
          </Typography>
        </Box>

        {/* Scrollable Permissions Container */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pr: 1,
            mt: 2,
            minHeight: 0,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#555',
              },
            },
          }}
        >
            {Object.keys(permissions).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('roles.addDialog.noPermissions')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.keys(permissions).map((category) => {
                  const moduleMetadata = getModuleMetadata(category);
                  return (
                    <Paper
                      key={category}
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                          }}
                        >
                          {moduleMetadata?.title || t(`roles.permissions.${category}`) || category}
                        </Typography>
                        {moduleMetadata?.description && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'block',
                              mt: 0.5,
                            }}
                          >
                            {moduleMetadata.description}
                          </Typography>
                        )}
                      </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1,
                      }}
                    >
                      {Object.keys(permissions[category]).map((permKey) => {
                        const permDetails = getPermissionDetails(category, permKey);
                        return (
                          <FormControlLabel
                            key={`${category}-${permKey}`}
                            control={
                              <Checkbox
                                checked={permissions[category][permKey]}
                                onChange={handlePermissionChange(category, permKey)}
                                disabled={isDefaultRole}
                                id={`${category}-${permKey}`}
                                name={`${category}-${permKey}`}
                                data-testid={`role-modal-checkbox-${category}-${permKey}`}
                                size="small"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {permDetails?.title || permKey}
                                </Typography>
                                {permDetails?.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {permDetails.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{
                              m: 0,
                              alignItems: 'flex-start',
                              '& .MuiCheckbox-root': {
                                pt: 0,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="role-modal-button-cancel"
        >
          {t('roles.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          disabled={!isFormValid() || Object.values(errors).some(error => error && error !== '')}
          id="confirm"
          name="confirm"
          data-testid="role-modal-button-confirm"
        >
          {editMode ? t('roles.editDialog.confirm') : t('roles.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddRoleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  roleData: PropTypes.object,
  defaultPermissions: PropTypes.array,
  modules: PropTypes.array,
  editMode: PropTypes.bool,
};

export default AddRoleModal;
